import Quill from 'quill';

const BuiltInLink = Quill.import('formats/link');

class CustomLink extends BuiltInLink {
    static create(value) {
        // value is either a Dialog object {href, title, target} or a plain string href
        const href = typeof value === 'object' && value !== null ? value.href || '' : value;
        const node = super.create(href);

        node.removeAttribute('target');
        node.removeAttribute('rel');

        if (typeof value === 'object' && value !== null) {
            if (value.title) {
                node.setAttribute('title', value.title);
            } else {
                node.removeAttribute('title');
            }

            if (value.target === '_blank') {
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
            } else if (value.target) {
                node.setAttribute('target', value.target);
            }
        }
        return node;
    }

    static formats(domNode) {
        return {
            href: domNode.getAttribute('href') || '',
            title: domNode.getAttribute('title') || '',
            target: domNode.getAttribute('target') || '',
        };
    }

    // Called by Quill's formatText on an already-existing link blot.
    // The built-in Link.format passes value straight to domNode.setAttribute,
    // which coerces any object to '[object Object]'. Extract href first.
    format(name, value) {
        if (name !== this.statics.blotName || !value) {
            return super.format(name, value);
        }
        const href = typeof value === 'object' && value !== null ? value.href || '' : value;
        this.domNode.setAttribute('href', this.constructor.sanitize(href));

        if (typeof value === 'object' && value !== null) {
            if (value.title) {
                this.domNode.setAttribute('title', value.title);
            } else {
                this.domNode.removeAttribute('title');
            }

            this.domNode.removeAttribute('target');
            this.domNode.removeAttribute('rel');
            if (value.target === '_blank') {
                this.domNode.setAttribute('target', '_blank');
                this.domNode.setAttribute('rel', 'noopener noreferrer');
            } else if (value.target) {
                this.domNode.setAttribute('target', value.target);
            }
        }
    }
}

Quill.register('formats/link', CustomLink, true);

const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = [
    '8pt', '9pt', '10pt', '11pt', '12pt', '13pt', '14pt', '16pt',
    '18pt', '20pt', '24pt', '28pt', '32pt', '36pt', '48pt', '72pt',
];
Quill.register(SizeStyle, true);

export const DEFAULT_FONT_SIZES = ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'];

export const parseFontSizes = (fixedFont) =>
    fixedFont ? fixedFont.split(' ') : DEFAULT_FONT_SIZES;

// Normalizes a raw link format value into a consistent {href, title, target} shape.
export const readLinkFormat = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'string') return { href: raw, title: '', target: '' };
    return { href: raw.href || '', title: raw.title || '', target: raw.target || '' };
};

// Expands a collapsed cursor position outward to span the full link blot at that index.
export const getLinkRange = (quill, cursorIndex) => {
    let start = cursorIndex;
    let end = cursorIndex;
    const docLength = quill.getLength() - 1;
    while (start > 0 && quill.getFormat(start - 1, 1).link) start--;
    while (end < docLength && quill.getFormat(end, 1).link) end++;
    return { index: start, length: end - start };
};

// Strips all HTML tags and &nbsp; entities then returns the plain-text character count.
export const calculateLiveLength = (htmlContent) => {
    if (!htmlContent || htmlContent === '<p><br></p>') return 0;
    return htmlContent
        .replace(/(<([^>]+)>)/gi, '')
        .replace(/(&nbsp;+)/gi, '')
        .length;
};

// Serialization-only transform: injects \n after block tags to match TinyMCE output; never touches the Quill DOM.
export const formatHtmlLikeTinyMCE = (htmlString) => {
    if (!htmlString || htmlString === '<p><br></p>') return '';

    return htmlString
        .replace(/(<ul>|<ol>)/gi, '$1\n')
        .replace(/(<\/p>|<\/li>|<\/ul>|<\/ol>)/gi, '$1\n')
        .replace(/(<br\s*\/?>)/gi, '$1\n')
        .replace(/\n{2,}/g, '\n')
        .trim();
};

export default Quill;
